import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'components/UI/V3/Button/Button';
import { ServiceNameSection } from 'components/Boarding/Form/Steps/Details/Sections/ServiceName/ServiceNameSection';
import { DescriptionSection } from 'components/Boarding/Form/Steps/Details/Sections/Description/DescriptionSection';
import { ColorCodeSection } from 'components/Boarding/Form/Steps/Details/Sections/ColorCode/ColorCodeSection';
import { ServiceFormsSection } from 'components/Service/Forms/ServiceFormsSection';
import { AssociatedForm } from 'model/Forms';
import { VehiclesSelectionSection } from './Sections/VehiclesSelection/VehiclesSelectionSection';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { OrganizationMarketplaceServicesInfo } from '@spike/marketplace-model';
import Service from 'model/Service';
import styles from './TransportationDetailsStep.module.scss';

interface TransportationDetailsFormData {
    name: string;
    description?: string;
    selectedVehicles: Array<number>;
    color?: string;
    forms?: AssociatedForm[];
}

interface TransportationDetailsStepProps {
    formData: TransportationDetailsFormData;
    marketplaces: Service['marketplaces'];
    serviceId?: number;
    onNext: (details: {
        name: string;
        description: string;
        selectedVehicles: Array<number>;
        color?: string;
        forms?: AssociatedForm[];
        marketplaces: Service['marketplaces'];
    }) => void;
}

const withVehicleIds = (
    marketplaces: Service['marketplaces'],
    selectedVehicles: Array<number>,
    organizationServicesInfo: Array<OrganizationMarketplaceServicesInfo>
): Service['marketplaces'] => {
    const hasAnyVehicleIds = marketplaces.some(marketplace => (marketplace.vehicleIds ?? []).length > 0);

    return marketplaces.map(marketplace => {
        const existingVehicleIds = marketplace.vehicleIds ?? [];

        if (hasAnyVehicleIds || selectedVehicles.length === 0) {
            return {
                ...marketplace,
                vehicleIds: existingVehicleIds
            };
        }

        const vehicleIds = new Set(
            (
                organizationServicesInfo.find(info => info.marketplace.id === marketplace.marketplaceId)?.vehicles ?? []
            ).map(vehicle => vehicle.id)
        );

        return {
            ...marketplace,
            vehicleIds: selectedVehicles.filter(vehicleId => vehicleIds.has(vehicleId))
        };
    });
};

export const TransportationDetailsStep: React.FC<TransportationDetailsStepProps> = ({
    formData,
    marketplaces: initialMarketplaces,
    serviceId,
    onNext
}) => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const [serviceName, setServiceName] = useState({ value: formData.name || '', error: '' });
    const [description, setDescription] = useState({ value: formData.description || '', error: '' });
    const [color, setColor] = useState<string | null>(formData.color || null);
    const [forms, setForms] = useState<AssociatedForm[]>(formData.forms || []);
    const [marketplaces, setMarketplaces] = useState<Service['marketplaces']>(() =>
        withVehicleIds(initialMarketplaces, formData.selectedVehicles || [], organizationServicesInfo)
    );

    const marketplaceIdsKey = initialMarketplaces.map(marketplace => marketplace.marketplaceId).join(',');
    const selectedVehiclesKey = (formData.selectedVehicles || []).join(',');

    useEffect(() => {
        setMarketplaces(prev => {
            const synced = withVehicleIds(
                initialMarketplaces,
                formData.selectedVehicles || [],
                organizationServicesInfo
            ).map(marketplace => {
                const existing = prev.find(item => item.marketplaceId === marketplace.marketplaceId);
                return {
                    ...marketplace,
                    vehicleIds: existing?.vehicleIds ?? marketplace.vehicleIds ?? []
                };
            });

            const same =
                prev.length === synced.length &&
                prev.every(
                    (marketplace, index) =>
                        marketplace.marketplaceId === synced[index].marketplaceId &&
                        (marketplace.vehicleIds ?? []).join(',') === (synced[index].vehicleIds ?? []).join(',')
                );

            return same ? prev : synced;
        });
        // organizationServicesInfo is intentionally omitted: the hook returns a new array every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketplaceIdsKey, selectedVehiclesKey]);

    const handleServiceNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setServiceName({
            value: e.target.value,
            error: e.target.value.length === 0 ? 'Service name is required' : ''
        });
    }, []);

    const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription({
            value: e.target.value,
            error: e.target.value.length > 400 ? 'Description must be less than 400 characters' : ''
        });
    }, []);

    const selectedVehicles = useMemo(
        () => Array.from(new Set(marketplaces.flatMap(marketplace => marketplace.vehicleIds ?? []))),
        [marketplaces]
    );

    const isValid = serviceName.value.trim().length > 0 && selectedVehicles.length > 0;

    const handleNext = () => {
        if (!isValid) return;

        onNext({
            name: serviceName.value,
            description: description.value,
            selectedVehicles,
            color: color || undefined,
            forms,
            marketplaces
        });
    };

    return (
        <div className={styles.detailsStep}>
            <ServiceNameSection
                value={serviceName.value}
                error={serviceName.error}
                onChange={handleServiceNameChange}
                className={styles.section}
            />

            <DescriptionSection
                value={description.value}
                error={description.error}
                onChange={handleDescriptionChange}
                className={styles.section}
            />

            <VehiclesSelectionSection
                marketplaces={marketplaces}
                onChangeMarketplaces={setMarketplaces}
                className={styles.section}
            />

            <ColorCodeSection
                color={color}
                onChange={setColor}
                className={styles.section}
            />
            <ServiceFormsSection
                forms={forms}
                serviceId={serviceId}
                serviceCategory="transportation"
                onChange={setForms}
            />

            <div className={styles.footer}>
                <Button
                    variant="primary"
                    size="large"
                    label="Next"
                    onClick={handleNext}
                    disabled={!isValid}
                />
            </div>
        </div>
    );
};
